import { v4 as uuidv4 } from 'uuid';

const DEMO_USER_KEY = 'demo_user_id';
const DEMO_USER_NAME_KEY = 'demo_user_name';
const DEMO_MODE_KEY = 'demo_mode_active';
const DEMO_PASSWORD_KEY = 'demo_password';

/**
 * Service for managing demo mode state and demo user identity
 * 
 * SECURITY NOTES:
 * - Demo mode uses a completely separate backend (REACT_APP_BACKEND_API_URL_DEMO)
 * - Demo users have IDs prefixed with "demo-" for backend identification
 * - Demo mode is mutually exclusive with real authentication
 * - Real authentication automatically disables demo mode
 */
class DemoService {
  /**
   * Check if demo mode is enabled (environment variable is set)
   * Also validates that demo URL is different from production URL
   */
  isDemoModeEnabled(): boolean {
    const demoUrl = process.env.REACT_APP_BACKEND_API_URL_DEMO;
    // const prodUrl = process.env.REACT_APP_BACKEND_API_URL;
    
    // Demo mode is only enabled if:
    // 1. Demo URL is set and not empty
    // 2. Demo URL is different from production URL (security check)
    if (!demoUrl || demoUrl.trim().length === 0) {
      return false;
    }
    
    // SECURITY: Ensure demo URL is different from production
    // if (demoUrl === prodUrl) {
    //   console.warn('[DemoService] SECURITY: Demo URL cannot be same as production URL');
    //   return false;
    // }
    
    return true;
  }

  /**
   * Check if user is currently in demo mode
   */
  isDemoModeActive(): boolean {
    if (!this.isDemoModeEnabled()) {
      return false;
    }
    return localStorage.getItem(DEMO_MODE_KEY) === 'true';
  }

  /**
   * Get the demo backend URL
   */
  getDemoBackendUrl(): string {
    return process.env.REACT_APP_BACKEND_API_URL_DEMO || '';
  }

  /**
   * Get the appropriate backend URL based on demo mode state
   */
  getActiveBackendUrl(): string {
    if (this.isDemoModeActive()) {
      return this.getDemoBackendUrl();
    }
    return process.env.REACT_APP_BACKEND_API_URL || '';
  }

  /**
   * Get or create a demo user ID
   * Format: demo-{uuid}
   */
  getDemoUserId(): string {
    const existing = localStorage.getItem(DEMO_USER_KEY);
    if (existing && existing.startsWith('demo-')) {
      return existing;
    }

    // Generate new demo user ID
    const newId = `demo-${uuidv4()}`;
    localStorage.setItem(DEMO_USER_KEY, newId);
    return newId;
  }

  /**
   * Get the demo user display name
   */
  getDemoUserName(): string {
    const existing = localStorage.getItem(DEMO_USER_NAME_KEY);
    if (existing) {
      return existing;
    }

    // Generate a friendly demo user name
    const demoNames = [
      'Demo Explorer',
      'Demo User',
      'Trial User',
      'Guest Explorer'
    ];
    const name = demoNames[Math.floor(Math.random() * demoNames.length)];
    localStorage.setItem(DEMO_USER_NAME_KEY, name);
    return name;
  }

  /**
   * Activate demo mode
   */
  activateDemoMode(): void {
    if (!this.isDemoModeEnabled()) {
      console.warn('[DemoService] Demo mode is not enabled in environment');
      return;
    }
    localStorage.setItem(DEMO_MODE_KEY, 'true');
    // Ensure we have a demo user ID
    this.getDemoUserId();
    this.getDemoUserName();
  }

  /**
   * Deactivate demo mode and optionally clear demo user data
   */
  deactivateDemoMode(clearUserData: boolean = false): void {
    localStorage.removeItem(DEMO_MODE_KEY);
    if (clearUserData) {
      localStorage.removeItem(DEMO_USER_KEY);
      localStorage.removeItem(DEMO_USER_NAME_KEY);
      localStorage.removeItem(DEMO_PASSWORD_KEY);
    }
  }

  /**
   * Check if a user ID belongs to a demo user
   */
  isDemoUser(userId: string): boolean {
    return userId.startsWith('demo-');
  }

  /**
   * Get demo user info for AuthContext
   */
  getDemoUserInfo(): {
    id: string;
    displayName: string;
    email: string;
    roles: string[];
  } {
    return {
      id: this.getDemoUserId(),
      displayName: this.getDemoUserName(),
      email: 'demo@example.com',
      roles: ['user']
    };
  }

  /**
   * Store demo password after successful validation
   */
  setDemoPassword(password: string): void {
    localStorage.setItem(DEMO_PASSWORD_KEY, password);
  }

  /**
   * Get stored demo password
   */
  getDemoPassword(): string | null {
    return localStorage.getItem(DEMO_PASSWORD_KEY);
  }

  /**
   * Check if demo password is stored
   */
  hasDemoPassword(): boolean {
    const password = this.getDemoPassword();
    return password !== null && password.length > 0;
  }

  /**
   * Clear demo password (used when password is invalid/rotated)
   */
  clearDemoPassword(): void {
    localStorage.removeItem(DEMO_PASSWORD_KEY);
  }

  /**
   * Validate demo password with backend
   * Returns true if password is valid, false otherwise
   */
  async validateDemoPassword(password: string): Promise<boolean> {
    const demoUrl = this.getDemoBackendUrl();
    if (!demoUrl) {
      console.error('[DemoService] Demo backend URL not configured');
      return false;
    }

    try {
      const response = await fetch(`${demoUrl}/api/public/demopassword`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });

      return response.ok;
    } catch (error) {
      console.error('[DemoService] Failed to validate demo password:', error);
      return false;
    }
  }

  /**
   * Validate stored password and return whether it's still valid
   * If invalid, clears the stored password
   */
  async validateStoredPassword(): Promise<boolean> {
    const password = this.getDemoPassword();
    if (!password) {
      return false;
    }

    const isValid = await this.validateDemoPassword(password);
    if (!isValid) {
      // Password is no longer valid (may have been rotated)
      this.clearDemoPassword();
    }
    return isValid;
  }
}

// Export singleton instance
export const demoService = new DemoService();
